import { useState, useMemo, useCallback } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UnifiedUsersList } from "@/components/users/UnifiedUsersList";
import { UnifiedUserCard } from "@/components/users/UnifiedUserCard";
import { BulkActionToolbar } from "@/components/users/BulkActionToolbar";
import RolesList from "@/components/rbac/RolesList";
import RoleWizard from "@/components/rbac/RoleWizard";
import CreateUserWizardDialog from "@/components/users/CreateUserWizardDialog";
import { useUnifiedUsers } from "@/hooks/useUnifiedUsers";
import { useRoles, useConsoleUsers } from "@/hooks/rbac";
import { Search, Download, Users, Shield, UserCog, MonitorSmartphone, List, FolderTree, UserPlus, Plus, IdCard } from "lucide-react";
import ViewToggle from "@/components/ViewToggle";
import { Role } from "@/types/rbac";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { UserType, UnifiedUser } from "@/types/unified-user";
import { useIsMobile } from "@/hooks/use-mobile";

export default function UsersAndRolesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { users, stats, isLoading, error: unifiedError } = useUnifiedUsers();
  const { data: roles = [], error: rolesError } = useRoles();
  const { data: consoleUsers = [], error: consoleError } = useConsoleUsers();

  const [searchTerm, setSearchTerm] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState<string>(searchParams.get('filter') || "all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");
  const [hasDevicesFilter, setHasDevicesFilter] = useState("all");
  const isMobile = useIsMobile();
  const [view, setView] = useState<"grid" | "list">(isMobile ? "grid" : "list");
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "users");
  const [selectedUsers, setSelectedUsers] = useState<UnifiedUser[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateRoleDialogOpen, setIsCreateRoleDialogOpen] = useState(false);
  const [selectedRoleForEdit, setSelectedRoleForEdit] = useState<Role | null>(null);

  // Handle selection changes from list
  const handleSelectionChange = useCallback((users: UnifiedUser[]) => {
    setSelectedUsers(users);
  }, []);

  // Clear selection
  const handleClearSelection = useCallback(() => {
    setSelectedUsers([]);
  }, []);

  // Get unique providers from users
  const providers = useMemo(() => {
    const providerSet = new Set<string>();
    users.forEach(user => {
      if (user.provider) providerSet.add(user.provider);
    });
    return Array.from(providerSet).sort();
  }, [users]);

  // Calculate role stats
  const roleStats = useMemo(() => {
    const totalRoles = roles.length;
    const customRoles = roles.filter(role => !role.is_system).length;
    const usersWithRoles = consoleUsers.length;
    const systemRoles = roles.filter(role => role.is_system).length;
    
    return {
      totalRoles,
      customRoles,
      usersWithRoles,
      systemRoles
    };
  }, [roles, consoleUsers]);

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.department?.toLowerCase().includes(searchLower) ||
        user.title?.toLowerCase().includes(searchLower);

      // User type filter
      const matchesType = userTypeFilter === "all" || 
        (userTypeFilter === "directory" && (user.type === "directory" || user.type === "both")) ||
        (userTypeFilter === "console" && (user.type === "console" || user.type === "both")) ||
        (userTypeFilter === "both" && user.type === "both");

      // Status filter
      const matchesStatus = statusFilter === "all" || user.status === statusFilter;

      // Provider filter
      const matchesProvider = providerFilter === "all" || user.provider === providerFilter;

      // Has devices filter
      const matchesDevices = hasDevicesFilter === "all" ||
        (hasDevicesFilter === "yes" && user.deviceCount > 0) ||
        (hasDevicesFilter === "no" && user.deviceCount === 0);

      return matchesSearch && matchesType && matchesStatus && matchesProvider && matchesDevices;
    });
  }, [users, searchTerm, userTypeFilter, statusFilter, providerFilter, hasDevicesFilter]);

  const queryError = unifiedError || rolesError?.message || consoleError?.message;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background w-full">
        <div className="w-full space-y-6 px-6 py-6">
          <Skeleton className="h-16 w-full" />
          <div className="grid gap-4 md:grid-cols-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="min-h-screen bg-background w-full flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center gap-4 py-10">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-lg font-semibold">Failed to load Users & Roles</h2>
            <p className="text-sm text-muted-foreground text-center">{queryError}</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background w-full">
      <div className="w-full space-y-6 px-6 py-6">
        {/* Header */}
        <PageHeader title="Users & Roles" description="Manage directory users, console admins, and role permissions">
          {activeTab === "users" ? (
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          ) : (
            <Button onClick={() => setIsCreateRoleDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Role
            </Button>
          )}
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </PageHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => {
          setActiveTab(value);
          setSearchParams({ tab: value });
        }} className="space-y-6">
          {/* Tab Switcher + View Toggle */}
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger 
                value="users"
                className="hover:bg-transparent hover:text-current"
              >
                <Users className="w-4 h-4 mr-2" />
                Users {stats.total > 0 && (
                  <span className="ml-1.5 text-muted-foreground">({stats.total})</span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="roles"
                className="hover:bg-transparent hover:text-current"
              >
                <Shield className="w-4 h-4 mr-2" />
                Roles {roleStats.totalRoles > 0 && (
                  <span className="ml-1.5 text-muted-foreground">({roleStats.totalRoles})</span>
                )}
              </TabsTrigger>
            </TabsList>
            
          {/* View Toggle */}
          <ViewToggle view={view} onViewChange={setView} />
          </div>

          <TabsContent value="users" className="space-y-4 mt-0">
            {/* User Statistics Cards */}
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="flex items-center p-6">
                  <Users className="h-8 w-8 text-info" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                    <p className="font-bold text-lg">{stats.total}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center p-6">
                  <IdCard className="h-8 w-8 text-info" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Directory Users</p>
                    <p className="font-bold text-lg">{stats.directory}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center p-6">
                  <Shield className="h-8 w-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Console Admins</p>
                    <p className="font-bold text-lg">{stats.console}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center p-6">
                  <MonitorSmartphone className="h-8 w-8 text-orange-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Total Devices</p>
                    <p className="font-bold text-lg">{stats.totalDevices}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card disableHover>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search users by name, email, department..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Filter Row */}
                  <div className="flex flex-wrap gap-3">
                    <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
                      <SelectTrigger className="w-full sm:w-40">
                        <SelectValue placeholder="User Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="directory">Directory Only</SelectItem>
                        <SelectItem value="console">Console Only</SelectItem>
                        <SelectItem value="both">Both Types</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full sm:w-32">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>

                    {providers.length > 0 && (
                      <Select value={providerFilter} onValueChange={setProviderFilter}>
                        <SelectTrigger className="w-full sm:w-40">
                          <SelectValue placeholder="Provider" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Providers</SelectItem>
                          {providers.map(provider => (
                            <SelectItem key={provider} value={provider}>
                              {provider}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    <Select value={hasDevicesFilter} onValueChange={setHasDevicesFilter}>
                      <SelectTrigger className="w-full sm:w-40">
                        <SelectValue placeholder="Has Devices" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="yes">With Devices</SelectItem>
                        <SelectItem value="no">Without Devices</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Users Display */}
            {view === "list" ? (
              <>
                {/* Bulk Action Toolbar */}
                <BulkActionToolbar
                  selectedUsers={selectedUsers}
                  onClearSelection={handleClearSelection}
                />
                
                <Card disableHover>
                  <CardContent className="pt-6">
                    <UnifiedUsersList 
                      users={filteredUsers}
                      showTypeColumn={userTypeFilter === "all"}
                      onSelectionChange={handleSelectionChange}
                    />
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredUsers.map(user => (
                  <UnifiedUserCard
                    key={user.id}
                    user={user}
                    onClick={() => navigate(`/admin/users-and-roles/${user.id}`)}
                  />
                ))}
                {filteredUsers.length === 0 && (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    No users found matching your criteria
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="roles" className="space-y-4 mt-0">
            {/* Role Statistics Cards */}
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="flex items-center p-6">
                  <Shield className="h-8 w-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Total Roles</p>
                    <p className="font-bold text-lg">{roleStats.totalRoles}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center p-6">
                  <UserCog className="h-8 w-8 text-success" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Custom Roles</p>
                    <p className="font-bold text-lg">{roleStats.customRoles}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center p-6">
                  <Users className="h-8 w-8 text-info" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">Users with Roles</p>
                    <p className="font-bold text-lg">{roleStats.usersWithRoles}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="flex items-center p-6">
                  <FolderTree className="h-8 w-8 text-orange-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-muted-foreground">System Roles</p>
                    <p className="font-bold text-lg">{roleStats.systemRoles}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <RolesList 
              searchQuery="" 
              view={view}
              showCreateButton={false}
              onCreateRole={() => setIsCreateRoleDialogOpen(true)}
            />
          </TabsContent>
        </Tabs>

        {/* Create User Wizard Dialog */}
        <CreateUserWizardDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
        />

        {/* Create Role Wizard Dialog */}
        <RoleWizard
          open={isCreateRoleDialogOpen}
          onOpenChange={setIsCreateRoleDialogOpen}
          role={selectedRoleForEdit}
        />
      </div>
    </div>
  );
}
